import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BellRing, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { errMsg, http } from "@/lib/api";

export function FollowBrand({ brand, followers = 0 }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [count] = useState(followers);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/alerts/subscribe", { email, brand_id: brand.id, consent });
      setDone(data?.already_active ? "active" : "pending");
      toast.success(data?.already_active ? `You already follow ${brand.name}` : "Check your inbox", { description: data?.already_active ? undefined : `Confirm the email we sent to ${email} to start ${brand.name} alerts.` });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="follow-brand-btn"
          className="group inline-flex items-center gap-2.5 rounded-full bg-ph-lime py-2 pl-2 pr-5 text-sm font-semibold text-ph-navy shadow-[0_14px_30px_-12px_rgba(182,255,92,0.6)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(182,255,92,0.5)]">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ph-navy/10">
            {done ? <Check className="h-4 w-4" /> : <BellRing className="h-4 w-4 group-hover:animate-[ph-ring_0.8s_ease-in-out]" />}
          </span>
          {done === "active" ? "Following" : done ? "Confirm by email" : "Get price alerts"}
          <span className="font-mono-ph text-[11px] font-normal opacity-90" data-testid="follow-count">{count} following</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(92vw,360px)] rounded-3xl p-5" data-testid="follow-popover">
        <AnimatePresence mode="popLayout" initial={false}>
          {done ? (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-4 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-ph-lime/20 text-ph-accent"><Check className="h-6 w-6" /></span>
              <p className="mt-3 font-display text-lg font-bold text-ph-ink" data-testid="follow-done-title">{done === "active" ? "You already follow this brand" : "One more step"}</p>
              <p className="mt-1 text-sm text-ph-muted">{done === "active" ? "Alerts are already on for this email." : `We emailed ${email}. Tap the confirm link in it to start alerts. The link works for 7 days.`}</p>
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <p className="font-display text-lg font-bold text-ph-ink">Follow {brand.name}</p>
              <p className="mt-1 text-sm text-ph-muted">Get an email when a {brand.name} price list is added or updated.</p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="follow-email-input"
                className="mt-4 h-11 w-full rounded-full border border-ph-line bg-ph-bg px-4 text-sm text-ph-ink outline-none focus:border-ph-accent/60 focus:shadow-[0_0_0_4px_rgb(var(--ph-accent)/0.12)]" />
              <label className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-ph-muted">
                <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="follow-consent-checkbox" className="mt-0.5 h-4 w-4 shrink-0 accent-[#6366F1]" />
                I agree to receive price-list alert emails for {brand.name}. I can unsubscribe at any time. See the <a href="/legal/privacy" className="underline">privacy policy</a>.
              </label>
              <button type="submit" disabled={busy} data-testid="follow-submit-btn"
                className="mt-3 flex h-11 w-full items-center justify-center gap-2 rounded-full bg-ph-lime text-sm font-semibold text-ph-navy disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Notify me
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </PopoverContent>
    </Popover>
  );
}
